/**
 * orcamentos-store — núcleo da Ferramenta de Orçamento (O2).
 *
 * Hooks TanStack Query pro `/api/v1/orcamentos`. O CÁLCULO é do backend: as
 * mutations devolvem o orçamento completo (cabeçalho + itens com subtotais +
 * resumo) e a gente só exibe — sem recalcular no frontend (evita divergência de
 * arredondamento). Gated por ORCAMENTO_ENABLED (a página só renderiza com a flag).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

export type OrcamentoStatus = 'baixo' | 'medio' | 'saudavel';

/** R1 (RESUMO-REGIME): regime de faturamento da cascata. */
export type RegimeFaturamento = 'direto' | 'revenda';

export interface OrcamentoListItem {
  id: string;
  titulo: string;
  empresaId: string;
  dealId: string | null;
  totalItens: number;
  valorTotalCentavos: number;
  status: OrcamentoStatus;
  criadoEmIso: string;
  atualizadoEmIso: string;
}

export interface OrcamentoHeader {
  id: string;
  empresaId: string;
  dealId: string | null;
  /** Orçamento v2: card (proposta) desta revisão + metadados da revisão. */
  cardId: string | null;
  revisao: number;
  vigente: boolean;
  nota: string | null;
  legado: boolean;
  descontoManualTipo: 'pct' | 'valor' | null;
  descontoManualValor: number;
  cardManutencaoId: string | null;
  /** M3: tipo/modelo de origem (null = orçamento livre). */
  tipoId: string | null;
  titulo: string;
  moDiariaCentavos: number;
  moDias: number;
  markupMoPct: number;
  condAvistaDescontoPct: number;
  condEntradaPct: number;
  condParceladoN: number;
  /** R1: regime gravado NESTA revisão (null = pré-mig 417 → direto). */
  cascataRegime: RegimeFaturamento | null;
  /** AC-6: mensalidades recorrentes (centavos). Entram no MRR do card (AC-8). */
  manutencaoMensalCentavos: number;
  softwareMensalCentavos: number;
  observacoes: string | null;
  criadoPor: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
}

export interface OrcamentoItemLinha {
  id: string;
  orcamentoId: string;
  /** M3: bloco a que o item pertence (null = item solto / orçamento livre). */
  blocoId: string | null;
  /**
   * 🔴 mig 593 — o SEGUNDO eixo: a divisão de custo (CFTV, Infraestrutura…).
   * ⚠️ **Não é o bloco.** O bloco padroniza (o kit que vem montado); a divisão
   * mede quanto do orçamento é de cada frente. Um item tem os dois, e eles
   * podem discordar — um cabo dentro de um "Kit CFTV" é infraestrutura.
   */
  divisaoId: string | null;
  codigoProduto: string | null;
  codigo: string | null;
  unidade: string | null;
  descricao: string;
  custoUnitarioCentavos: number;
  markupPct: number;
  quantidade: number;
  ordem: number;
  totalCustoCentavos: number;
  vendaUnitCentavos: number;
  totalVendaCentavos: number;
  lucroCentavos: number;
  /** 🔴 #348 (mig 638): quem confirmou o preço de venda R$ 0,00, e quando. null = ainda não confirmado (a tela avisa). */
  precoZeroConfirmadoPor?: string | null;
  precoZeroConfirmadoEmIso?: string | null;
}

export interface OrcamentoResumo {
  custoMaterialCentavos: number;
  vendaMaterialCentavos: number;
  lucroMaterialCentavos: number;
  custoMoCentavos: number;
  vendaMoCentavos: number;
  lucroMoCentavos: number;
  /** VALOR TOTAL = material + M.O. (SEM kit — é o que vai pro deal). */
  valorTotalCentavos: number;
  custoTotalCentavos: number;
  lucroTotalCentavos: number;
  /** @deprecated R2 (RESUMO-REGIME): margem legada (markup sobre custo). O
   * número do sistema agora é a margem LÍQUIDA da cascata. Fica só por compat
   * de payload — nenhuma tela deve exibi-la. */
  margemPct: number;
  /** B3: margem do produto/material em % (lucro ÷ venda do material). */
  margemProdutoPct?: number;
  /** B3: margem da mão de obra em % (lucro ÷ venda da M.O.). */
  margemMoPct?: number;
  /** B3: kit insumo — totais e margem (0 quando não há kit). */
  custoKitCentavos?: number;
  vendaKitCentavos?: number;
  lucroKitCentavos?: number;
  margemKitPct?: number;
  /** B3: total COM kit (campo novo — o legado segue sem kit). */
  valorTotalComKitCentavos?: number;
  custoTotalComKitCentavos?: number;
  lucroTotalComKitCentavos?: number;
  margemTotalComKitPct?: number;
  markupMaterial: number;
  status: OrcamentoStatus;
  top5Custos: Array<{ index: number; totalCustoCentavos: number }>;
  pagamento: {
    avistaCentavos: number;
    entradaCentavos: number;
    parcelaCentavos: number;
    /** Fix A1: última parcela absorve o resto do rateio (= parcela quando exato). */
    ultimaParcelaCentavos: number;
    parceladoN: number;
  };
  /** CA-4: cascata de margem (interno; null sem parâmetros). Vem PRONTA do backend. */
  cascata?: {
    impDescFornCentavos: number;
    impostoMatCentavos: number;
    lucroMatCentavos: number;
    impostoMdoCentavos: number;
    lucroMdoCentavos: number;
    lucroBrutoCentavos: number;
    vendaTotalCentavos: number;
    /** null = comissão OCULTA (não-admin) — URGENTE 05/08: a cascata vai a
     *  todos até a margem líquida; só a comissão é interna. */
    comissaoCentavos: number | null;
    lucroLiquidoCentavos: number;
    /** PARTE C: alíquotas EFETIVAS por perna (o rótulo da conta usa estas). */
    aliqMatEfetivaPct: number;
    aliqMdoEfetivaPct: number;
    margemBrutaPct: number;
    margemLiquidaPct: number;
    saudavel: boolean;
    params: {
      aliqFornecedorPct: number;
      aliqEmpresaPct: number;
      /** Item 3/PARTE C: alíquota de serviço e as EMPRESAS de origem (pior caso). */
      aliqServicoPct?: number | null;
      aliqProdutoEmpresa?: string | null;
      aliqServicoEmpresa?: string | null;
      /** null = comissão oculta (não-admin). */
      comissaoPct: number | null;
      margemSaudavelMin: number;
      fornecedorPiorNome?: string | null;
      /** R1: regime com que ESTA cascata foi calculada. */
      regime?: RegimeFaturamento;
    };
  } | null;
}

export interface DealVinculo {
  id: string;
  humanLabel: string;
  cliente: string;
}

/** M3: bloco do orçamento (snapshot do Modelo) + sua checklist de sugestões. */
export interface OrcamentoBlocoSugestaoLinha {
  id: string;
  blocoId: string;
  nome: string;
  ordem: number;
  /** Item real vinculado quando preenchida (null = só lembrete). */
  itemId: string | null;
}

export interface OrcamentoBlocoLinha {
  id: string;
  orcamentoId: string;
  nome: string;
  tipoBloco: 'fixo' | 'repetivel';
  ordem: number;
  instanciaNum: number;
  modeloBlocoId: string | null;
  sugestoes: OrcamentoBlocoSugestaoLinha[];
}

/** 🔴 mig 593: uma divisão de custo do catálogo (lista ÚNICA, global). */
export interface DivisaoLinha {
  id: string;
  nome: string;
  ordem: number;
  ativo: boolean;
}

/**
 * Uma linha do indicador por divisão.
 *
 * ⚠️ `divisaoId: null` é a linha **"Sem divisão"**, e ela aparece sempre que
 * houver item sem divisão — é o que impede o número de parecer completo quando
 * não está.
 */
export interface LinhaPorDivisao {
  divisaoId: string | null;
  nome: string;
  valorCentavos: number;
  itens: number;
  pct: number;
}

export interface OrcamentoFull {
  orcamento: OrcamentoHeader;
  itens: OrcamentoItemLinha[];
  resumo: OrcamentoResumo;
  deal: DealVinculo | null;
  /** M3: estrutura de blocos (vazio = orçamento livre). */
  blocos: OrcamentoBlocoLinha[];
  /** 🔴 mig 593: o catálogo ATIVO de divisões (o seletor). Lista única. */
  divisoes: DivisaoLinha[];
  /**
   * 🔴 mig 593: o indicador — quanto do orçamento é de cada divisão.
   *
   * ⚠️ **Vem VAZIO quando nenhum item tem divisão**, e a tela usa isso para NÃO
   * mostrar o quadro num orçamento antigo — uma lista de zeros pareceria
   * defeito.
   */
  porDivisao: LinhaPorDivisao[];
  /** Orçamento v2: vínculo com a proposta (null = orçamento legado/avulso). */
  card: {
    id: string;
    humanLabel: string;
    cliente: string;
    clienteNumUnidades: number | null;
    fechado: boolean;
    orcamentoAceitoId: string | null;
  } | null;
  /** Orçamento v2: revisão aceita congelada no Ganho (só leitura). */
  congelado: boolean;
  /** O4: faixa de parcelamento sugerida pro total atual (ajustável). */
  faixaSugerida: { entradaPct: number; parcelas: number } | null;
  /** R1: trava do seletor de regime no builder + padrão global vigente. */
  regimeConfig?: { permitir: boolean; padrao: RegimeFaturamento };
}

export interface ProdutoBusca {
  codigoProduto: string;
  codigo: string | null;
  descricao: string;
  unidade: string | null;
  custoUnitarioCentavos: number;
  /** 🔴 #348: o vendedor já confirmou, na escolha, que o produto vai a R$ 0,00 (custo zero no Omie). */
  precoZeroConfirmado?: true;
}

// ─── Simuladores (O3) ────────────────────────────────────────────────────

export interface SicoobParcela {
  prazo: number;
  taxaPct: number;
  parcelaCentavos: number;
  totalCentavos: number;
}

export interface GrenkeParcela {
  prazo: number;
  fatorPct: number;
  financiadoCentavos: number;
  parcelaFinanciamentoCentavos: number;
  mensalCentavos: number;
}

export interface GrenkeInput {
  equipamentosCentavos: number;
  instalacaoCentavos: number;
  servicoMensalCentavos: number;
  softwareMensalCentavos: number;
}

export interface EmpresaLite {
  id: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  ativo?: boolean;
}

const KEY = ['orcamentos'] as const;

/** GET /api/v1/empresas — pro seletor de empresa (CNPJ dos produtos). */
export function useEmpresas() {
  return useQuery({
    queryKey: ['empresas', 'list'] as const,
    queryFn: () => api.get<EmpresaLite[]>('/api/v1/empresas'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

/** GET /api/v1/orcamentos (lista). `dealId` filtra. */
export function useOrcamentos(dealId?: string) {
  return useQuery({
    queryKey: [...KEY, 'list', dealId ?? null] as const,
    queryFn: () =>
      api.get<{ items: OrcamentoListItem[] }>(
        `/api/v1/orcamentos${dealId ? `?dealId=${encodeURIComponent(dealId)}` : ''}`
      ),
    enabled: hasToken(),
  });
}

/** GET /api/v1/orcamentos/:id (cabeçalho + itens + resumo). */
export function useOrcamentoFull(id: string | null) {
  return useQuery({
    queryKey: [...KEY, 'full', id] as const,
    queryFn: () => api.get<OrcamentoFull>(`/api/v1/orcamentos/${id}`),
    enabled: hasToken() && !!id,
  });
}

/** GET /api/v1/orcamentos/produtos — busca de produtos do Omie (custo). */
export function useProdutoBusca(empresaId: string | null, q: string) {
  return useQuery({
    queryKey: [...KEY, 'produtos', empresaId, q] as const,
    queryFn: () =>
      // 🔴 #360: `total` = quantos casaram antes do limite — a tela diz "mostrando 50 de 69" (o MESMO motor do levantamento)
      api.get<{ items: ProdutoBusca[]; total?: number }>(
        `/api/v1/orcamentos/produtos?empresa_id=${encodeURIComponent(empresaId ?? '')}&q=${encodeURIComponent(q)}`
      ),
    enabled: hasToken() && !!empresaId && q.trim().length >= 2,
  });
}

export interface DealBusca {
  id: string;
  numeroHumano: number | null;
  humanLabel: string;
  cliente: string;
  valorCentavos: number;
  fase: string;
}

/** GET /api/v1/deals/buscar — busca cards de venda pra vincular ao orçamento (O4). */
export function useBuscarDeals(q: string) {
  return useQuery({
    queryKey: ['deals', 'buscar', q] as const,
    queryFn: () =>
      api.get<{ items: DealBusca[] }>(
        `/api/v1/deals/buscar?workflow=comercial&q=${encodeURIComponent(q)}`
      ),
    enabled: hasToken() && q.trim().length >= 2,
  });
}

/** GET /api/v1/orcamentos/:id/simular/sicoob — financiamento do VALOR TOTAL. */
export function useSimularSicoob(id: string | null, valorCentavos?: number) {
  return useQuery({
    queryKey: [...KEY, 'sicoob', id, valorCentavos ?? null] as const,
    queryFn: () =>
      api.get<{ valorCentavos: number; parcelas: SicoobParcela[] }>(
        `/api/v1/orcamentos/${id}/simular/sicoob${
          valorCentavos != null ? `?valorCentavos=${Math.round(valorCentavos)}` : ''
        }`
      ),
    enabled: hasToken() && !!id,
  });
}

/** POST /api/v1/orcamentos/simular/grenke — locação (inputs ad-hoc). */
export function useSimularGrenke() {
  return useMutation({
    mutationFn: (input: GrenkeInput) =>
      api.post<{ parcelas: GrenkeParcela[] }>('/api/v1/orcamentos/simular/grenke', input),
  });
}

// ─── Kit insumo (C5) ─────────────────────────────────────────────────────

export interface KitInsumo {
  id: string;
  orcamentoId: string;
  descricao: string;
  valorCentavos: number;
  /** % de markup sobre o valor (40 = 40%). OC-4. */
  markupPct: number;
  /** Venda derivada = valor × (1 + markup/100), em centavos. OC-4. */
  vendaCentavos: number;
  ordem: number;
  criadoEmIso: string;
}

/** GET /api/v1/orcamentos/:id/kit-insumo — carteira de imprevistos do orçamento. */
export function useOrcamentoKitInsumo(orcamentoId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'kit-insumo', orcamentoId] as const,
    queryFn: () => api.get<{ items: KitInsumo[] }>(`/api/v1/orcamentos/${orcamentoId}/kit-insumo`),
    enabled: hasToken() && !!orcamentoId,
  });
}

export function useKitInsumoActions(orcamentoId: string) {
  const qc = useQueryClient();
  /**
   * 🔴 KIT-ATUALIZA-NA-HORA (31/08/2026) — o VALOR TOTAL passa a mudar na hora.
   *
   * Antes isto invalidava SÓ a query do kit, e o `full` — de onde vem o valor
   * total, o custo e a margem — ficava com o número velho até um refresh. As
   * rotas passaram a responder o orçamento completo (mesmo helper das rotas de
   * item), então aqui é só gravar no cache do `full`, como `useOrcamentoActions`
   * já fazia. Um caminho só para "o orçamento mudou".
   */
  const aplicar = (full: OrcamentoFull) => {
    qc.setQueryData([...KEY, 'full', orcamentoId], full);
    qc.invalidateQueries({ queryKey: [...KEY, 'kit-insumo', orcamentoId] });
    qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
  };
  const add = useMutation({
    mutationFn: (input: { descricao: string; valorCentavos: number; markupPct?: number }) =>
      api.post<OrcamentoFull>(`/api/v1/orcamentos/${orcamentoId}/kit-insumo`, input),
    onSuccess: aplicar,
  });
  const remove = useMutation({
    mutationFn: (kitId: string) =>
      api.delete<OrcamentoFull>(`/api/v1/orcamentos/${orcamentoId}/kit-insumo/${kitId}`),
    onSuccess: aplicar,
  });
  // 🔴 CADASTRO-RAPIDO-E-MARKUP (31/08/2026): a mutation `recalcular` saiu
  // junto com o botão que a chamava. O markup do kit é congelado no momento da
  // adição — não há mais o que reaplicar.
  return { add, remove };
}

export interface CreateOrcamentoInput {
  empresaId: string;
  titulo: string;
  dealId?: string | null;
  /** M3 (legado): tipo (opcional — o servidor resolve o modelo vinculado). */
  tipoId?: string | null;
  /** A7: modelo NOMEADO a aplicar (opcional — sem modelo = orçamento livre). */
  modeloId?: string | null;
}

/** AO-5: payload tipado pra adicionar um item (antes `Record<string, unknown>`). */
export interface OrcamentoItemInput {
  descricao: string;
  codigoProduto?: string | null;
  codigo?: string | null;
  unidade?: string | null;
  custoUnitarioCentavos: number;
  /** F3 (HIGIENE-FUSO): opcional — omitido, o servidor usa o markup padrão de
   * material CONFIGURADO (ou responde 422 se não houver). Nunca 40 hardcoded. */
  markupPct?: number;
  quantidade: number;
  /** M3: bloco a que o item pertence (omitido/null = item solto). */
  blocoId?: string | null;
  /**
   * 🔴 mig 593/597: a divisão que ele ESCOLHEU — o gesto explícito. Ele mexeu
   * no seletor nesta sessão, ou clicou na divisão da linha. **Ganha de tudo**,
   * inclusive da memória do produto.
   */
  divisaoId?: string | null;
  /**
   * 🔴 mig 597: a divisão que estava no campo por INÉRCIA — herdada do último
   * item, sem toque dele. Só vale quando o produto não tem memória própria.
   *
   * ⚠️ **Não é o valor do campo que decide, é a ORIGEM dele.** Os dois podem
   * carregar o mesmo id e mandarem coisas diferentes: por isso são dois campos
   * e não um, e por isso a tela nunca manda os dois ao mesmo tempo.
   */
  divisaoSugerida?: string | null;
}

/** AO-5: campos editáveis de um item + lock otimista B9 (opcional). */
export interface OrcamentoItemPatch {
  descricao?: string;
  custoUnitarioCentavos?: number;
  markupPct?: number;
  quantidade?: number;
  /** 🔴 mig 593: trocar a divisão pelo rótulo da linha. `null` TIRA a divisão. */
  divisaoId?: string | null;
  /** B9 (AO-2): versão do item que o cliente tinha ao editar (409 se velha). */
  expectedAtualizadoEmIso?: string;
  /** 🔴 #348: o vendedor confirmou o preço de venda R$ 0,00 deste item (grava quem e quando; sozinho, não edita nada). */
  precoZeroConfirmado?: true;
}

/** AO-5: patch do cabeçalho + lock otimista B9 (opcional). */
export type UpdateHeaderPatch = Partial<OrcamentoHeader> & {
  expectedAtualizadoEmIso?: string;
};

/** Mutations — criam/editam e devolvem o orçamento completo. */
export function useOrcamentoActions() {
  const qc = useQueryClient();
  const invalidate = (id?: string) => {
    qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
    if (id) qc.invalidateQueries({ queryKey: [...KEY, 'full', id] });
  };
  const setFull = (full: OrcamentoFull) => {
    qc.setQueryData([...KEY, 'full', full.orcamento.id], full);
    qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
  };

  const create = useMutation({
    mutationFn: (input: CreateOrcamentoInput) =>
      api.post<OrcamentoFull>('/api/v1/orcamentos', input),
    onSuccess: (full) => setFull(full),
  });
  const updateHeader = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateHeaderPatch }) =>
      api.put<OrcamentoFull>(`/api/v1/orcamentos/${id}`, patch),
    onSuccess: (full) => setFull(full),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/v1/orcamentos/${id}`),
    onSuccess: (_r, id) => invalidate(id),
  });
  // Item 1 dos ADENDOS (08/09): aplicar a atualização de preços/impostos —
  // sempre DEPOIS da prévia (o dialog mostra o de→para antes deste POST).
  const atualizarPrecos = useMutation({
    mutationFn: (id: string) =>
      api.post<OrcamentoFull>(`/api/v1/orcamentos/${id}/atualizar-precos`, {}),
    onSuccess: (full) => setFull(full),
  });
  const addItem = useMutation({
    mutationFn: ({ id, item }: { id: string; item: OrcamentoItemInput }) =>
      api.post<OrcamentoFull>(`/api/v1/orcamentos/${id}/itens`, item),
    onSuccess: (full) => setFull(full),
  });
  const updateItem = useMutation({
    mutationFn: ({
      id,
      itemId,
      patch,
    }: {
      id: string;
      itemId: string;
      patch: OrcamentoItemPatch;
    }) => api.patch<OrcamentoFull>(`/api/v1/orcamentos/${id}/itens/${itemId}`, patch),
    onSuccess: (full) => setFull(full),
  });
  const removeItem = useMutation({
    mutationFn: ({ id, itemId }: { id: string; itemId: string }) =>
      api.delete<OrcamentoFull>(`/api/v1/orcamentos/${id}/itens/${itemId}`),
    onSuccess: (full) => setFull(full),
  });
  // M3.2: preencher uma sugestão de bloco (vira item real + liga a sugestão).
  const fillSugestao = useMutation({
    mutationFn: ({
      id,
      sugestaoId,
      item,
    }: {
      id: string;
      sugestaoId: string;
      item: OrcamentoItemInput;
    }) =>
      api.post<OrcamentoFull>(`/api/v1/orcamentos/${id}/sugestoes/${sugestaoId}/preencher`, item),
    onSuccess: (full) => setFull(full),
  });
  // M3.3: blocos repetíveis — adicionar nova instância / remover instância.
  const addBlocoInstancia = useMutation({
    mutationFn: ({ id, blocoId }: { id: string; blocoId: string }) =>
      api.post<OrcamentoFull>(`/api/v1/orcamentos/${id}/blocos/${blocoId}/adicionar`, {}),
    onSuccess: (full) => setFull(full),
  });
  const removeBloco = useMutation({
    mutationFn: ({ id, blocoId }: { id: string; blocoId: string }) =>
      api.delete<OrcamentoFull>(`/api/v1/orcamentos/${id}/blocos/${blocoId}`),
    onSuccess: (full) => setFull(full),
  });

  return {
    create,
    updateHeader,
    remove,
    atualizarPrecos,
    addItem,
    updateItem,
    removeItem,
    fillSugestao,
    addBlocoInstancia,
    removeBloco,
  };
}

// ─── 🔴 mig 593: o catálogo de divisões de custo ─────────────────────────
//
// ⚠️ Lista ÚNICA da empresa (decisão do Matheus, 13/09): indicador que varia
// de lista em lista não compara obra com obra.

const KEY_DIVISOES = ['orcamento-divisoes'] as const;

/** GET /divisoes — o catálogo. `incluirInativas` só na tela de cadastro. */
export function useDivisoes(incluirInativas = false) {
  return useQuery({
    queryKey: [...KEY_DIVISOES, incluirInativas],
    queryFn: () =>
      api.get<{ divisoes: DivisaoLinha[] }>(
        `/api/v1/orcamentos/divisoes${incluirInativas ? '?inativas=1' : ''}`
      ),
    staleTime: 60_000,
  });
}

/**
 * Criar / renomear / (in)ativar divisão.
 *
 * 🔴 Invalida TAMBÉM os orçamentos abertos: o seletor e o indicador saem do
 * `GET /orcamentos/:id`, e renomear sem invalidar deixaria o nome velho na tela.
 */
export function useDivisoesActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: KEY_DIVISOES });
    qc.invalidateQueries({ queryKey: [...KEY, 'full'] });
  };

  const criar = useMutation({
    mutationFn: (nome: string) =>
      api.post<{ divisao: DivisaoLinha }>('/api/v1/orcamentos/divisoes', { nome }),
    onSuccess: invalidate,
  });
  const atualizar = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { nome?: string; ativo?: boolean } }) =>
      api.patch<{ divisoes: DivisaoLinha[]; itens: number }>(
        `/api/v1/orcamentos/divisoes/${id}`,
        patch
      ),
    onSuccess: invalidate,
  });

  return { criar, atualizar };
}
